import { ColDef, RowClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL1020_R01Store } from "./Store_SAL1020_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultSAL1020_R01HeaderDto, SAL1020_R01HeaderDto } from "./Dto_SAL1020_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL1020_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL1020_R01HeaderDto> | null>;
}

export const Table_SAL1020_R01 = (props: Props) => {

	const { gridRef } = props;

	const { SAL1020_R01List, 
		selectedSAL1020_R01, setSelectedSAL1020_R01, initSelectedSAL1020_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSAL1020_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'salesOrderNo',
		gridData: SAL1020_R01List,
		defaultGridData: defaultSAL1020_R01HeaderDto,
		selectedRow: selectedSAL1020_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		orderDt: '수주일자',
		customCd: '거래처',
		customNm: '상호',
		vatTypeNm: '부가세',
		empNm: '수주담당',
		logicCenterNm: '출고물류',
		orderAmt: '매출액',
		vatAmt: '부가세',
		totalAmt: '매출총액',
		dueDt: '납기',
		approveFlg: '승인여부',
		telNo: '전화',
		faxNo: '팩스',
		email: 'e-mail',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<SAL1020_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 80,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수주일자', colId: 'orderDt', field: 'orderDt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderDt'),
			valueFormatter: (p: ValueFormatterParams<SAL1020_R01HeaderDto>) =>
				p.data?.orderDt ? formatDate(new Date(p.data.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처', colId: 'customCd', field: 'customCd', width: 100,
			cellStyle: (p) => getCellStyle(p, 'customCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 150,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '부가세', colId: 'vatTypeNm', field: 'vatTypeNm', width: 80,
			cellStyle: (p) => getCellStyle(p, 'vatTypeNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수주담당', colId: 'empNm', field: 'empNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'empNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '출고물류', colId: 'logicCenterNm', field: 'logicCenterNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'logicCenterNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매출액', colId: 'orderAmt', field: 'orderAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'orderAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '부가세', colId: 'vatAmt', field: 'vatAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'vatAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '매출총액', colId: 'totalAmt', field: 'totalAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'totalAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '납기', colId: 'dueDt', field: 'dueDt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'dueDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: ValueFormatterParams<SAL1020_R01HeaderDto>) =>
				p.data?.dueDt ? formatDate(new Date(p.data.dueDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '승인여부', colId: 'approveFlg', field: 'approveFlg', width: 80,
			cellStyle: (p) => getCellStyle(p, 'approveFlg'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value === 'Y' ? 'Y' : 'N',
		},
		{
			headerName: '전화', colId: 'telNo', field: 'telNo', width: 120,
			cellStyle: (p) => getCellStyle(p, 'telNo'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '팩스', colId: 'faxNo', field: 'faxNo', width: 120,
			cellStyle: (p) => getCellStyle(p, 'faxNo'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: 'e-mail', colId: 'email', field: 'email', width: 180,
			cellStyle: (p) => getCellStyle(p, 'email'),
			headerClass: aggridHeaderClass,
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL1020_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL1020_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SAL1020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 수주정보와 같은지 확인
		const isSelected = selectedSAL1020_R01?.salesOrderNo === rowData.salesOrderNo;
		if (isSelected) {
			setSelectedSAL1020_R01(undefined);
			setDetailScrollTop(null);
		} else {
			setDetailScrollTop(null);
			setSelectedSAL1020_R01(rowData);
		}
	}, [selectedSAL1020_R01, setSelectedSAL1020_R01, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: SAL1020_R01List.length.toLocaleString() + "건",
		orderAmt: SAL1020_R01List.reduce((sum, item) => (sum + item.orderAmt), 0).toLocaleString(),
		vatAmt: SAL1020_R01List.reduce((sum, item) => (sum + item.vatAmt), 0).toLocaleString(),
		totalAmt: SAL1020_R01List.reduce((sum, item) => (sum + item.totalAmt), 0).toLocaleString(),
		editable: false,
	}]), [SAL1020_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL1020_R01List, selectedSAL1020_R01]);

	return (
		<GridContextWrapper<SAL1020_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL1020_R01}
			defaultRowData={defaultSAL1020_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL1020_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SAL1020_R01HeaderDto>({
					idKey: 'salesOrderNo',
					selectedList: selectedSAL1020_R01 ? [selectedSAL1020_R01.salesOrderNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}

